/**
 * Counter words for backend values. Presentation only: statuses, tender codes and error codes come from the
 * backend and are never invented here. Raw backend messages and technical codes are never shown.
 */
import { isApiError } from '@/api/errors';
import type { BadgeTone } from '@/components/Badge';
import { formatLocation, isLocationType } from '@/venue/venue';

/** "Table 12" or "Seat B12". A location type this app does not know is shown as its bare label. */
export function locationText(type: string, label: string): string {
  return isLocationType(type) ? formatLocation(type, label) : label;
}

/** "ORD-0042" → "#0042". */
export function orderNumberText(orderNumber: string): string {
  const match = /^ORD-(\d+)$/.exec(orderNumber);
  return match ? `#${match[1]}` : orderNumber;
}

function humanize(code: string): string {
  const text = code.toLowerCase().replace(/_/g, ' ').trim();
  return text === '' ? 'Unknown' : text.charAt(0).toUpperCase() + text.slice(1);
}

// ─── Tenders ─────────────────────────────────────────────────────────────────

const TENDER_LABELS: Record<string, string> = {
  CASH: 'Cash',
  CARD_TERMINAL: 'Card',
  CARD_MANUAL: 'Card',
  UPI: 'UPI',
  UPI_QR: 'UPI',
  GATEWAY_ONLINE: 'Online',
};

/** A known tender code in plain words; any other code the branch configured is shown readably. */
export function tenderLabel(code: string): string {
  return TENDER_LABELS[code] ?? humanize(code);
}

// ─── Statuses ────────────────────────────────────────────────────────────────

type Tone = Exclude<BadgeTone, 'waiter'>;

/** The bill's `settlement_status`. "PENDING" on a bill means a balance is still due. */
export function settlementView(status: string): { label: string; tone: Tone } {
  switch (status) {
    case 'PENDING':
      return { label: 'Balance due', tone: 'attention' };
    case 'SETTLED':
      return { label: 'Paid in full', tone: 'success' };
    case 'CLOSED':
      return { label: 'Closed', tone: 'neutral' };
    default:
      return { label: humanize(status), tone: 'neutral' };
  }
}

/** One payment's own status, which is not the bill's. */
export function paymentView(status: string): { label: string; tone: Tone } {
  switch (status) {
    case 'SETTLED':
      return { label: 'Paid', tone: 'success' };
    case 'PENDING':
      return { label: 'Not completed', tone: 'attention' };
    case 'FAILED':
      return { label: 'Failed', tone: 'danger' };
    default:
      return { label: humanize(status), tone: 'neutral' };
  }
}

// ─── Help requests ───────────────────────────────────────────────────────────

/** The same words the waiter floor and the kitchen use for an open help request. */
const ASSISTANCE: Record<string, string> = {
  CALL_WAITER: 'Needs a waiter',
  WATER: 'Water',
  CUTLERY: 'Cutlery',
  BILL: 'Wants the bill',
  OTHER: 'Other request',
};

export const assistanceLabel = (type: string): string => ASSISTANCE[type] ?? humanize(type);

export function sessionStatusLabel(status: string): string {
  switch (status) {
    case 'ACTIVE':
      return 'Open';
    case 'SETTLED':
      return 'Settled';
    case 'CLOSED':
      return 'Closed';
    default:
      return humanize(status);
  }
}

export function adjustmentLabel(type: string): string {
  switch (type) {
    case 'AUTHORIZED_DISCOUNT':
      return 'Discount';
    case 'COMPLIMENTARY_DISCOUNT':
      return 'Complimentary';
    default:
      return humanize(type);
  }
}

export function formatDuration(minutes: number): string {
  if (!Number.isFinite(minutes) || minutes < 1) return 'Just opened';
  if (minutes < 60) return `${Math.floor(minutes)} min`;
  const hours = Math.floor(minutes / 60);
  const rest = Math.floor(minutes % 60);
  return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`;
}

/** "10:42" in the device's own time format. */
export function formatClock(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

// ─── Errors ──────────────────────────────────────────────────────────────────

/** Where an error came from, so the same backend code can be explained for that action. */
export type CounterAction = 'load' | 'payment' | 'adjustment' | 'print' | 'close' | 'open';

function detailAmount(error: { details?: Record<string, unknown> }, key: string): number | null {
  const value = error.details?.[key];
  return typeof value === 'number' && Number.isSafeInteger(value) ? value : null;
}

/**
 * Plain words for a failed counter action. `formatAmount` writes a server figure from the error (for
 * example the remaining balance the backend reports); it never calculates one.
 */
export function describeCounterError(
  error: unknown,
  action: CounterAction,
  formatAmount?: (minor: number) => string,
): string {
  if (!isApiError(error)) return 'Something went wrong. Please try again.';
  if (error.isNetworkError) {
    return action === 'load'
      ? "Couldn't reach the server. Check the connection and try again."
      : "Couldn't confirm whether that went through. Check the bill before trying again.";
  }
  if (error.status === 401) return 'Your session has ended. Please sign in again.';
  if (error.status === 403) {
    if (action === 'adjustment') return 'Only a branch manager can adjust a bill.';
    if (action === 'close' && error.hasCode('UNAUTHORIZED_ROLE'))
      return 'Only a branch manager can close a session this way.';
    return "You're not allowed to do that.";
  }

  if (error.hasCode('PAYMENT_EXCEEDS_BALANCE')) {
    const remaining = detailAmount(error, 'remaining_balance');
    return remaining !== null && formatAmount
      ? `That is more than the balance due. The balance due is ${formatAmount(remaining)}. Enter an amount up to that.`
      : 'That is more than the balance due. Enter a smaller amount.';
  }
  if (error.hasCode('BILL_ALREADY_SETTLED'))
    return 'This bill has no balance left to pay. It has been refreshed.';
  if (error.hasCode('TENDER_NOT_ACCEPTED'))
    return "That payment method isn't accepted here. Choose one of the methods shown.";
  if (error.hasCode('SESSION_ALREADY_CLOSED'))
    return 'This session has already been closed. The list has been refreshed.';
  if (error.hasCode('DUPLICATE_IDEMPOTENCY_KEY'))
    return 'That request was already used for something different. Please try again.';
  if (error.hasCode('BILL_NOT_FOUND'))
    return 'There is no bill for this session yet. Nothing has been ordered.';
  if (error.hasCode('SESSION_NOT_FOUND')) return 'That session could not be found.';
  if (error.hasCode('INVALID_ADJUSTMENT'))
    return "That adjustment is larger than the bill, so it wasn't applied.";
  if (error.hasCode('BILL_BELOW_SETTLED'))
    return "That would take the bill below what's already been paid, so it wasn't applied.";
  if (error.hasCode('BILL_NOT_SETTLED'))
    return 'The bill still has a balance due, so the session was not closed.';
  if (error.hasCode('SESSION_HAS_UNSERVED_ITEMS'))
    return 'Some items have not been served yet, so the session was not closed.';
  if (error.hasCode('SESSION_HAS_PENDING_PAYMENTS'))
    return 'An online payment is still pending, so the session was not closed.';
  if (error.hasCode('NO_PRINTER_CONFIGURED'))
    return 'No counter printer is set up for this branch.';
  if (error.hasCode('PRINTER_NOT_FOUND')) return 'The counter printer could not be found.';
  if (error.hasCode('LOCATION_NOT_FOUND')) return 'That table or seat could not be found.';
  if (
    error.hasCode('TABLE_CAPACITY_EXCEEDED') ||
    error.hasCode('LOCATION_OCCUPIED') ||
    error.hasCode('LOCATION_UNAVAILABLE')
  )
    return "That table or seat can't take another session right now.";
  if (error.status === 400 || error.status === 422)
    return 'Please check what you entered and try again.';
  if (error.status === 409)
    return 'The bill changed. It has been refreshed. Please check it and try again.';
  if (error.status >= 500) return 'Something went wrong on our side. Try again in a moment.';
  return "That didn't go through. Please try again.";
}

/** One sentence for a finished bill print, from the print job's own status. `ok` is false for a failure. */
export function describePrint(
  status: string,
  isReprint: boolean,
): { ok: boolean; message: string } {
  const what = isReprint ? 'Reprint' : 'Bill';
  switch (status) {
    case 'SENT':
      return {
        ok: true,
        message: isReprint ? 'Bill reprint sent to the printer.' : 'Bill sent to the printer.',
      };
    case 'PENDING':
      return { ok: true, message: `${what} queued. It has not printed yet.` };
    case 'FAILED':
      return {
        ok: false,
        message: `The bill did not print. Check the counter printer, then try again.`,
      };
    default:
      return { ok: false, message: 'The print status is unknown. Check the printer.' };
  }
}

/** Accepted tenders listed in a `TENDER_NOT_ACCEPTED` refusal, or null when the refusal carries none. */
export function acceptedTendersFrom(error: unknown): string[] | null {
  if (!isApiError(error) || !error.hasCode('TENDER_NOT_ACCEPTED')) return null;
  const raw = error.details?.accepted_tenders;
  if (!Array.isArray(raw)) return null;
  return raw.filter((t): t is string => typeof t === 'string' && t.length > 0);
}
